import React, { useCallback, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import sweetalert from 'sweetalert2'
import { useSelector, useDispatch } from 'react-redux'
import fetchIncomingMails from '../../sagas/fetchIncomingMails'
import { createSelector } from 'reselect'
import AddAddress from '../../Sidebar/Settings/components/IncomingEmails/AddAddress'
import { navigationController } from 'components/Modal'
import { LeftSidebarView, ViewBody } from 'components/Sidebar'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import {
  ButtonGroup,
} from 'react-bootstrap'
import Button from 'components/Button'
import { Formik } from 'formik'
import requestSendEmailFromCard from '../../sagas/requestSendEmailFromCard'
import trimRichText from 'components/RichText/utils/trim'
import {
  FormGroup,
  ControlLabel,
  HelpBlock,
  FormControl,
  InputGroup,
} from 'react-bootstrap'
import {
  ListItem
} from 'components/List'
import KanBoIcon from 'components/KanBoIcon'
import isEmail from 'validator/es/lib/isEmail'
import useGetData from 'hooks/useGetData'
import styled from 'styled-components'

const LoadingKanBoIcon = styled(KanBoIcon)`
  margin-top: 5px;
`

type Props = LeftSidebarProps & {
  modalId: string
  cardId: number
  content: any
  clearInput: () => void
}

const SendEmailFromCard: React.FC<Props> = ({
  modalId,
  leftSidebar,
  cardId,
  content,
  clearInput,
}) => {
  const intl = useIntl()

  const dispatch = useDispatch()

  const { push } = navigationController.useActions()

  const incomingMailsLoading = useSelector(state => state['view-card'].incomingMails[cardId].isLoading)

  const incomingMailsLoaded = useSelector(state => state['view-card'].incomingMails[cardId].isLoaded)

  const publicIncomingMailName = useSelector<any>(useMemo(() => createSelector(
    [
      state => state.kanbo.incomingMails.names,
      state => state.kanbo.incomingMails.byName,
    ],
    (
      incomingMailNames,
      incomingMailsByName,
    ) =>
      incomingMailNames.find(name => incomingMailsByName[name].cardId === cardId)
  ), [cardId]))

  const close = useCallback(() => {
    leftSidebar.remove(modalId)
  }, [modalId])

  const args = useMemo(() => ({
    cardId,
  }), [cardId])
  const {
    data,
  } : {
    data: any
  } = useGetData('SuggestMailAddressesForCard', args)

  useEffect(() => {
    if (!incomingMailsLoading && !incomingMailsLoaded) {
      dispatch(fetchIncomingMails({
        cardId
      }))
    }
  }, [incomingMailsLoading, incomingMailsLoaded])

  useEffect(() => {
    if (incomingMailsLoaded && !publicIncomingMailName) {
      sweetalert({
        type: 'info',
        text: intl.formatMessage({ id: 'INCOMING_EMAILS_CARD_ADDRESS_GET_NOT_CREATED' }),
        confirmButtonText: intl.formatMessage({ id: 'YES' }),
        cancelButtonText: intl.formatMessage({ id: 'NO' }),
        showCancelButton: true,
      }).then(() => {
        push((props) => <AddAddress {...props} cardId={cardId} />)
      }).catch(sweetalert.noop)

      close()
    }
  }, [publicIncomingMailName, incomingMailsLoaded])

  return (
    <LeftSidebarView
      title={<FormattedMessage id='CARD_COMMENT_SEND_EMAIL_FROM_CARD' />}
    >
      <Formik
        initialValues={{
          email: '',
          emails: new Set<string>(),
        }}
        onSubmit={async ({ emails }) => {
          try {
            await dispatch(requestSendEmailFromCard({
              cardId,
              message: trimRichText(content),
              emails: Array.from(emails)
            }))

            clearInput()
          } finally {
            leftSidebar.remove(modalId)
          }
        }}
      >
        {({ handleSubmit, handleChange, values, errors, isSubmitting, setValues, setFieldError }) => (
          <form onSubmit={handleSubmit}>
            <FormGroup
              validationState={errors.email ? 'error' : null}
            >
              <ViewBody>
                <InputGroup>
                  <FormattedMessage id='CARD_COMMENT_SEND_EMAIL_FROM_CARD_EMAIL_PLACEHOLDER'>
                    {formattedMessage =>
                      <FormControl
                        autoFocus
                        type='text'
                        name='email'
                        value={values.email}
                        placeholder={formattedMessage}
                        onChange={handleChange}
                      />
                    }
                  </FormattedMessage>
                  <InputGroup.Button>
                    <Button
                      onClick={() => {
                        if (values.email) {
                          if (isEmail(values.email)) {
                            setValues({
                              ['emails']: new Set(values.emails).add(values.email),
                              ['email']: '',
                            })
                          } else {
                            setFieldError('email', intl.formatMessage({ id: 'CARD_COMMENT_SEND_EMAIL_FROM_CARD_EMAIL_INVALID' }))
                          }
                        } else {
                          setFieldError('email', intl.formatMessage({ id: 'CARD_COMMENT_SEND_EMAIL_FROM_CARD_EMAIL_EMPTY' }))
                        }
                      }}
                    >
                      <FormattedMessage id='CARD_COMMENT_SEND_EMAIL_FROM_CARD_EMAIL_ADD' />
                    </Button>
                  </InputGroup.Button>
                </InputGroup>
                {errors.email && <HelpBlock>{errors.email}</HelpBlock>}
              </ViewBody>            
              {data ? (
                data.Addresses.filter(email => !values.emails.has(email)).map(email =>
                  <Button
                    key={email}
                    bsStyle='link'
                    onClick={() => {
                      setValues({
                        ['emails']: new Set(values.emails).add(email),
                        ['email']: values.email,
                      })
                    }}
                  >
                    {email}
                  </Button>  
                )
              ) : (
                <ViewBody><LoadingKanBoIcon spin name='kanbo-16-loading' /></ViewBody> 
              )}  
            </FormGroup>
            <FormGroup>
              <ViewBody>
                <ControlLabel><FormattedMessage id='CARD_COMMENT_SEND_EMAIL_FROM_CARD_EMAILS' /></ControlLabel>
              </ViewBody>
              {Array.from(values.emails).map(email =>
                <ListItem
                  key={email}
                  onClick={() => {}}
                  menu={[{
                    icon: <KanBoIcon fixedWidth name='kanbo-16-remove' />,
                    title: <FormattedMessage id='REMOVE' />,
                    onClick: () => {
                      const newEmails = new Set(values.emails)

                      newEmails.delete(email)

                      setValues({
                        ['emails']: newEmails,
                        ['email']: values.email,
                      })
                    }
                  }]}
                >
                  {email}
                </ListItem>  
              )}
              {values.emails.size === 0 && <ViewBody><HelpBlock><i><FormattedMessage id='CARD_COMMENT_SEND_EMAIL_FROM_CARD_EMAILS_EMPTY' /></i></HelpBlock></ViewBody>}
            </FormGroup>
            <ViewBody>             
              <ButtonGroup>
                <Button
                  type='submit'
                  bsStyle='primary'
                  disabled={isSubmitting || values.emails.size === 0}
                >
                  <FormattedMessage id='CARD_COMMENT_SEND' />
                </Button>
                <Button
                  onClick={close}
                >
                  <FormattedMessage id='CANCEL' />
                </Button>
              </ButtonGroup>
            </ViewBody> 
          </form>
        )}
      </Formik>
    </LeftSidebarView>
  )
}

export default withLeftSidebar(SendEmailFromCard)
